import React, { useEffect, Suspense, lazy } from 'react';
import { RouteObject } from 'react-router';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import * as _ from 'lodash';

import { AppDispatch, RootState, useAppSelector } from './state/store';
import {
  getIsAuthenticated,
  setCurrentPage,
  validateSessionAsync
} from './state/auth/authSlice';
import {
  fetchUserDetailsAsync,
  getLoading,
  getLoggedInUserData
} from './state/user/userSlice';
import {
  isAdmin,
  isDoctor,
  isAssistant,
  retrieveDataFromLocalStorage,
  updateAllowedRoutes,
  isTrialUser
} from './utils';
import SidebarLayout from './layouts/SidebarLayout';
import BaseLayout from './layouts/BaseLayout';
import SuspenseLoader from './components/SuspenseLoader';
import UnauthorizedAccessPage from './components/Unauthorised';
import ProgressLoader from './components/Loader';
import { ROLES } from './constants';
import { LocationState } from './content/applications/Subscriptions/PaymentDetails/Payments';
import { getSelectedSubscription } from './state/subscriptions/subscriptionSlice';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const {
    role,
    officeId,
    practiceLocationEnabled,
    subscription: { _id, hasExpired }
  } = useSelector((state: RootState) => state.user.userData);
  const { subscriptionId } = useAppSelector(getSelectedSubscription);
  const dispatch = useDispatch<AppDispatch>();
  const tokens = retrieveDataFromLocalStorage([
    'accessToken',
    'refreshToken',
    'sikkaToken',
    'stripePublishKey'
  ]);
  useEffect(() => {
    dispatch(setCurrentPage(window.location.pathname));
    if (!isAuthenticated && tokens.accessToken) {
      (async () => {
        await dispatch(validateSessionAsync(tokens));
      })();
    }
    if (tokens.accessToken) {
      (async () => {
        await dispatch(
          fetchUserDetailsAsync({ accessToken: tokens.accessToken })
        );
      })();
    }
  }, [isAuthenticated, officeId, practiceLocationEnabled, role]);
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const fromCheckoutPage = !!locationState?.fromCheckout;

  let allowedRoutes = [
    '/login',
    '/signUp',
    '/accounts/profile',
    '/accounts/roles',
    '/accounts/permissions',
    '/accounts/assign-roles',
    '/subscriptions/plan',
    '/subscriptions/payment',
    '/reports/all',
    '/dashboard',
    '/ai-costing',
    '/training',
    '/resources',
    '/support',
    '/installation'
  ];
  const hasNoTokens = !isAuthenticated && !tokens.accessToken;

  // update allowed routes based on officeId,practiceLocation & role
  let updatedAllowedRoutes: string[] = allowedRoutes;

  if (officeId === null) {
    updatedAllowedRoutes = updateAllowedRoutes(allowedRoutes, [
      '/accounts/roles',
      '/accounts/permissions',
      '/accounts/assign-roles',
      '/reports/all'
    ]);
    allowedRoutes = updatedAllowedRoutes;
  }

  if (hasNoTokens) {
    return <Navigate to="/login" replace />;
  }

  if (window.location.pathname === '/login') {
    return <>{children}</>;
  }
  if (role && officeId !== '' && practiceLocationEnabled !== null) {
    if (
      isAdmin(role) &&
      officeId !== null &&
      practiceLocationEnabled === true
    ) {
      updatedAllowedRoutes = [
        ...updateAllowedRoutes(allowedRoutes, [
          '/accounts/profile',
          '/reports/all',
          '/subscriptions/plan',
          '/subscriptions/payment'
        ]),
        '/users/trial/all',
        '/users/trial/create',
        '/users/all',
        '/reports/trial-users',
        '/copilot-batch-processor',
        '/training',
        '/resources',
        '/admin/resources',
        '/support',
        '/installation'
      ];
    } else if (isDoctor(role) || isAssistant(role)) {
      updatedAllowedRoutes = [
        ...updateAllowedRoutes(allowedRoutes, [
          '/accounts/roles',
          '/accounts/permissions',
          '/accounts/assign-roles'
        ]),
        '/users/update-profile',
        '/users/change-password',
        // '/users/invoices',
        '/users/billing-invoices',
        '/account-settings/notification-preferences',
        '/account-settings/mfa-setting',
        '/users/update-practice',
        '/plans/cdt-codes',
        '/plans/internal-codes',
        '/staff-contacts',
        '/faq',
        '/resources',
        REFERRAL_ROUTE
      ];
      // // show GPS when profile complete and subscription active (match SidebarMenu)
      if (officeId && practiceLocationEnabled && _id && !hasExpired) {
        updatedAllowedRoutes.push('/gps');
      }
    } else if (
      isTrialUser(role) &&
      officeId !== null &&
      practiceLocationEnabled === true
    ) {
      updatedAllowedRoutes = [
        ...updateAllowedRoutes(allowedRoutes, [
          '/accounts/roles',
          '/accounts/permissions',
          '/accounts/assign-roles',
          '/reports/all',
          '/subscriptions/payment',
          '/dashboard'
        ]),
        '/resources',
        '/faq',
        '/training',
        '/support',
        '/installation',
        REFERRAL_ROUTE
      ];
    }
    allowedRoutes = [...updatedAllowedRoutes];

    // Learning hub + schedule pages stay available for any authenticated portal role
    // that reaches this guard (covers roles that skip the branches above).
    for (const route of [
      '/resources',
      '/faq',
      '/training',
      '/support',
      '/installation'
    ]) {
      if (!allowedRoutes.includes(route)) {
        allowedRoutes.push(route);
      }
    }

    // // User without subscription tries to visit payment method page(or came from checkout but had refreshed page manually)
    if (
      !isAdmin(role) &&
      !_id &&
      (!fromCheckoutPage || (fromCheckoutPage && !subscriptionId)) &&
      window.location.pathname === '/subscriptions/payment'
    ) {
      return <Navigate to="/subscriptions/plan" replace />;
    }

    if (
      (isDoctor(role) || isAssistant(role) || isTrialUser(role)) &&
      (officeId === null || practiceLocationEnabled === false) &&
      [
        '/accounts/roles',
        '/accounts/permissions',
        '/accounts/assign-roles',
        '/reports/all',
        '/dashboard'
      ].includes(window.location.pathname)
    ) {
      return <Navigate to="/accounts/profile" replace />;
    }

    const pathname = (location.pathname.replace(/\/+$/, '') || '/') as string;
    if (!allowedRoutes.includes(pathname)) {
      return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
  } else {
    return <ProgressLoader loading={true} />;
  }
};

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

const Dashboard = Loader(
  lazy(() => import('./content/applications/Dashboards'))
);

// const UserProfile = Loader(
//   lazy(() => import('src/content/applications/Accounts/profile'))
// );
// const UserSettings = Loader(
//   lazy(() => import('src/content/applications/Accounts/settings'))
// );

const ManageRoles = Loader(
  lazy(() => import('./content/applications/Accounts/Roles'))
);

const ManagePermissions = Loader(
  lazy(() => import('./content/applications/Accounts/Permissions'))
);

const AssignRoles = Loader(
  lazy(() => import('./content/applications/Accounts/AssignRoles'))
);

const ManageProfile = Loader(
  lazy(() => import('./content/applications/Accounts/Profile'))
);

const SignUp = Loader(lazy(() => import('src/content/auth/signup')));

const Login = Loader(lazy(() => import('src/content/auth/login')));

const ForgotPassword = Loader(
  lazy(() => import('src/content/auth/ForgotPassword'))
);

const ResetPassword = Loader(
  lazy(() => import('src/content/auth/ResetPassword'))
);

// const ReportsAll = Loader(lazy(() => import('./content/applications/Reports/AllReportsList')))

const ReportDetail = Loader(
  lazy(() => import('./content/applications/Reports/ReportDetail'))
);
const ReportsAll = Loader(
  lazy(() => import('./content/applications/Reports/DentistReports'))
);

const SubscriptionAll = Loader(
  lazy(() => import('./content/applications/Subscriptions/PlanList'))
);

const Payments = Loader(
  lazy(() => import('./content/applications/Subscriptions/PaymentDetails'))
);

const CreateTrialUsers = Loader(
  lazy(() => import('./content/applications/TrialUsers/CreateTrialUser/index'))
);

const TrialUsersAll = Loader(
  lazy(() => import('./content/applications/TrialUsers/TrialUsersList/index'))
);

const AllUsers = Loader(
  lazy(() => import('./content/applications/Users/UsersList/index'))
);

const AiCosting = Loader(
  lazy(() => import('./content/applications/AiCosting/PracticeList/index'))
);

const PageNotFound = Loader(lazy(() => import('./components/PageNotFound')));

const UpdateProfile = Loader(
  lazy(() => import('./content/applications/Users/UpdateProfile'))
);

const UpdatePassword = Loader(
  lazy(() => import('./content/applications/Users/UpdatePassword'))
);

const NextBilling = Loader(
  lazy(() => import('./content/applications/Users/NextBilling'))
);

const Invoices = Loader(
  lazy(() => import('./content/applications/Users/Invoices'))
);

const NotificationPreferences = Loader(
  lazy(
    () =>
      import('./content/applications/AccountSettings/NotificationPreferences')
  )
);

const MFASetting = Loader(
  lazy(() => import('./content/applications/AccountSettings/MFASetting'))
);

const UpdatePractice = Loader(
  lazy(() => import('./content/applications/Users/UpdatePractice'))
);

const TrialUsersReports = Loader(
  lazy(() => import('./content/applications/Reports/TrialUsersReports'))
);

const UserDetails = Loader(
  lazy(() => import('./content/applications/Users/UserDetails'))
);

const CDTCodes = Loader(
  lazy(() => import('./content/applications/Plans/CDTCodes'))
);

const InternalCodes = Loader(
  lazy(() => import('./content/applications/Plans/InternalCodes'))
);

const CopilotBatchProcessor = Loader(
  lazy(() => import('./content/applications/CopilotBatchProcessor'))
);

const Training = Loader(lazy(() => import('./content/applications/Training')));
const Support = Loader(lazy(() => import('./content/applications/Support')));
const Installation = Loader(lazy(() => import('./content/applications/Installation')));
const Referral = Loader(lazy(() => import('./content/applications/Referral')));
const FAQ = Loader(lazy(() => import('./content/applications/FAQ')));
const LearningResources = Loader(
  lazy(() => import('./content/applications/LearningResources')),
);
const REFERRAL_ROUTE = '/referral';

const GPS = Loader(lazy(() => import('./content/applications/GPS')));
const StaffContacts = Loader(
  lazy(() => import('./content/applications/StaffContacts')),
);
const AdminLearningResources = Loader(
  lazy(() => import('./content/applications/AdminLearningResources')),
);

const AdminOnly = ({ children }) => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const { role, officeId, practiceLocationEnabled } =
    useAppSelector(getLoggedInUserData);
  const dispatch = useDispatch<AppDispatch>();
  const tokens = retrieveDataFromLocalStorage([
    'accessToken',
    'refreshToken',
    'sikkaToken',
    'stripePublishKey'
  ]);
  const userLoading = useAppSelector(getLoading('user'));
  const authLoading = useAppSelector(getLoading('auth'));
  useEffect(() => {
    dispatch(setCurrentPage(window.location.pathname));
    if (!isAuthenticated && tokens.accessToken) {
      (async () => {
        await dispatch(validateSessionAsync(tokens));
      })();
    }
    if (tokens.accessToken) {
      (async () => {
        await dispatch(
          fetchUserDetailsAsync({ accessToken: tokens.accessToken })
        );
      })();
    }
  }, [isAuthenticated, officeId, practiceLocationEnabled, role]);

  if (role && role !== ROLES.ADMIN) {
    return <Navigate to="/unauthorized" replace />;
  }
  if (!userLoading && !authLoading) {
    return <>{children}</>;
  }
  return <ProgressLoader loading={true} />;
};

const routes: RouteObject[] = [
  {
    path: 'signUp',
    element: (
      <BaseLayout>
        <SignUp />
      </BaseLayout>
    )
  },
  {
    path: 'login',
    element: (
      <BaseLayout>
        <Login />
      </BaseLayout>
    )
  },
  {
    path: 'forgot-password',
    element: (
      <BaseLayout>
        <ForgotPassword />
      </BaseLayout>
    )
  },
  {
    path: 'reset-password',
    element: (
      <BaseLayout>
        <ResetPassword />
      </BaseLayout>
    )
  },
  {
    path: 'unauthorized',
    element: (
      <BaseLayout>
        <UnauthorizedAccessPage />
      </BaseLayout>
    )
  },
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="login" />
      },
      {
        path: 'dashboard',
        element: (
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'accounts',
    element: <SidebarLayout />,
    children: [
      // {
      //   path: '',
      //   element: <Navigate to="details" replace />
      // },
      // {
      //   path: 'details',
      //   element: <AuthGuard><UserProfile /></AuthGuard>
      // },
      // {
      //   path: 'settings',
      //   element: <AuthGuard><UserSettings /></AuthGuard>
      // },
      {
        path: '',
        element: <Navigate to="roles" replace />
      },
      {
        path: 'roles',
        element: (
          <AuthGuard>
            <ManageRoles />
          </AuthGuard>
        )
      },
      {
        path: 'permissions',
        element: (
          <AuthGuard>
            <ManagePermissions />
          </AuthGuard>
        )
      },
      {
        path: 'assign-roles',
        element: (
          <AuthGuard>
            <AssignRoles />
          </AuthGuard>
        )
      },
      {
        path: 'profile',
        element: (
          <AuthGuard>
            <ManageProfile />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'reports',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="all" replace />
      },
      {
        path: 'all',
        element: (
          <AuthGuard>
            <ReportsAll />
          </AuthGuard>
        )
      },
      {
        path: 'trial-users',
        element: (
          <AuthGuard>
            <TrialUsersReports />
          </AuthGuard>
        )
      },
      {
        path: ':id',
        element: (
          <AuthGuard>
            <ReportDetail />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'subscriptions',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="plan" replace />
      },
      {
        path: 'plan',
        element: (
          <AuthGuard>
            <SubscriptionAll />
          </AuthGuard>
        )
      },
      {
        path: 'payment',
        element: (
          <AuthGuard>
            <Payments />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'users',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="all" replace />
      },
      {
        path: 'all',
        element: (
          <AuthGuard>
            <AllUsers />
          </AuthGuard>
        )
      },
      {
        path: 'trial/all',
        element: (
          <AuthGuard>
            <TrialUsersAll />
          </AuthGuard>
        )
      },
      {
        path: 'trial/create',
        element: (
          <AuthGuard>
            <CreateTrialUsers />
          </AuthGuard>
        )
      },
      {
        path: 'update-profile',
        element: (
          <AuthGuard>
            <UpdateProfile />
          </AuthGuard>
        )
      },
      {
        path: 'change-password',
        element: (
          <AuthGuard>
            <UpdatePassword />
          </AuthGuard>
        )
      },
      {
        path: 'update-practice',
        element: (
          <AuthGuard>
            <UpdatePractice />
          </AuthGuard>
        )
      },
      {
        path: 'billing-invoices',
        element: (
          <AuthGuard>
            <NextBilling />
          </AuthGuard>
        )
      },
      {
        path: 'details',
        element: (
          <AdminOnly>
            <UserDetails />
          </AdminOnly>
        )
      }
      // {
      //   path: 'invoices',
      //   element: <AuthGuard><Invoices /></AuthGuard>
      // },
    ]
  },
  {
    path: 'account-settings',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="notification-preferences" replace />
      },
      {
        path: 'notification-preferences',
        element: (
          <AuthGuard>
            <NotificationPreferences />
          </AuthGuard>
        )
      },
      {
        path: 'mfa-setting',
        element: (
          <AuthGuard>
            <MFASetting />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'plans',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="cdt-codes" replace />
      },
      {
        path: 'cdt-codes',
        element: (
          <AuthGuard>
            <CDTCodes />
          </AuthGuard>
        )
      },
      {
        path: 'internal-codes',
        element: (
          <AuthGuard>
            <InternalCodes />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'gps',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <GPS />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'staff-contacts',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <StaffContacts />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'admin/resources',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <AdminOnly>
              <AdminLearningResources />
            </AdminOnly>
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'ai-costing',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <AiCosting />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'copilot-batch-processor',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <CopilotBatchProcessor />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'referral',
    element: (
      <AuthGuard>
        <Referral />
      </AuthGuard>
    )
  },
  {
    path: 'training',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <Training />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'support',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <Support />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'installation',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <Installation />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'faq',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <FAQ />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: 'resources',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: (
          <AuthGuard>
            <LearningResources />
          </AuthGuard>
        )
      }
    ]
  },
  {
    path: '*',
    element: <PageNotFound />
  }
];

export default routes;
